import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  CardActions,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { nameofFactory } from '@/shared/services';

import { HookFormTextField } from '../../forms/components';
import { ClipModel } from '../types';
import TrackedObject from '@/features/teams/components/TrackedObject';
import { TeamModel } from '@/features/teams/types/TeamModel';
import { X as TimesIcon, Save as SaveIcon, Hash, Video as Videocam } from 'lucide-react';
import { usePTZStore } from '@/features/camera/store/usePTZStore';
import PlayerCameraFeedSelectionButton from '@/features/video/components/PlayerCameraFeedSelectionButton';
type Props = {
  clip?: ClipModel; // Optional for creating new clips
  teams: TeamModel[];
  onSubmit: (newClipModel: ClipModel) => Promise<void>;
  onCancel: (newClipModel?: ClipModel) => void;
  onDelete?: (clip: ClipModel) => void; // For deleting new clips
  isLocalClip?: boolean; // Flag to determine if this is a local clip
};

export type ClipFormValues = {
  note: string;
  description: string;
  objectTrackingId: number;
  cameraId: string;
  cameraMode: 'tracked' | 'fixed';
};

const nameOfForm = nameofFactory<ClipFormValues>();

const DESCRIPTION_MAX = 512;

const validationSchema: Yup.ObjectSchema<ClipFormValues> = Yup.object().shape({
  [nameOfForm('note')]: Yup.string().required().max(128).min(1).label('Name'),
  [nameOfForm('description')]: Yup.string().optional().max(DESCRIPTION_MAX).label('Description'),
  // Conditional validation: required only when cameraMode is 'tracked'
  [nameOfForm('objectTrackingId')]: Yup.number().when('cameraMode', {
    is: 'tracked',
    then: (schema) => schema.required().min(1).label('Tracked Object'),
    otherwise: (schema) => schema.optional(),
  }),
  [nameOfForm('cameraId')]: Yup.string().required().min(1).label('Camera'),
  [nameOfForm('cameraMode')]: Yup.string()
    .oneOf(['tracked', 'fixed'])
    .required()
    .label('Camera Mode'),
}) as Yup.ObjectSchema<ClipFormValues>;

const ClipRecordForm = ({ clip, onSubmit, onCancel, teams, isLocalClip = false }: Props) => {
  // Determine if we're creating a new clip or editing existing one
  const { getCurrentPTZ } = usePTZStore();

  // Get editing model from provided clip, or create default for new clip
  const editingModel: Partial<ClipModel> = clip || {
    id: '',
    note: '',
    description: '',
    objectTrackingId: 0,
    cameraId: 0,
    cameraMode: 'tracked',
  };

  // popover to change tracked entity
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const trackedEntityRef = useRef(null);

  const [disabled, setDisabled] = useState(false);
  // Track error messages for tracked object switching
  const [trackedObjectError, setTrackedObjectError] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(trackedEntityRef.current);
    return event;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const form = useForm<ClipFormValues>({
    defaultValues: {
      note: editingModel.note || '',
      description: editingModel.description || '',
      objectTrackingId: editingModel.objectTrackingId || 0,
      cameraId: editingModel.cameraId || '',
      cameraMode: (editingModel.cameraMode as 'tracked' | 'fixed') || 'tracked',
    },
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const handleSubmit = form.handleSubmit(async (formValues) => {
    try {
      const ptz = getCurrentPTZ ? getCurrentPTZ() : { pan: -1, tilt: -1, zoom: -1 };
      if (formValues.cameraMode === 'fixed') {
        if (ptz.pan === -1 && ptz.tilt === -1 && ptz.zoom === -1) {
          throw new Error('PTZ values are not set for fixed camera mode.');
        }
      }

      setDisabled(true);

      const updateClipModel = {
        ...editingModel,
        ...formValues,
        ...ptz,
        // For new clips, generate a temporary ID if needed
        id: editingModel.id || `temp_${Date.now()}`,
      } as ClipModel;

      await onSubmit(updateClipModel);

      setDisabled(false);
    } catch (e) {
      console.error('Error submitting Clip Record Form:', e);
      setDisabled(false);
    }
  });

  const handleCancel = () => {
    const currentValues = form.getValues();
    onCancel({ ...editingModel, ...currentValues } as ClipModel);
  };

  const descriptionLength = form.watch(nameOfForm('description'))?.length || 0;

  const handleChangeTrackedEntity = (objectTrackingId: number) => {
    form.setValue(nameOfForm('objectTrackingId'), objectTrackingId);
    handleClose();
  };

  const handleCameraChange = (cameraId: string) => {
    form.setValue(nameOfForm('cameraId'), cameraId);
  };

  const handleCameraModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'tracked' | 'fixed' | null,
  ) => {
    if (newMode === null) return; // Don't allow deselection

    form.setValue(nameOfForm('cameraMode'), newMode);

    // When switching to "fixed" mode, set objectTrackingId to -1
    if (newMode === 'fixed') {
      form.setValue(nameOfForm('objectTrackingId'), -1);
      setTrackedObjectError(null); // Clear any previous errors
    }
    // When switching back to "tracked" mode, reset to 0 (invalid, user must select)
    else if (newMode === 'tracked') {
      form.setValue(nameOfForm('objectTrackingId'), 0);
    }
  };

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            {/* Header Section */}
            <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ gap: 0.75, display: 'flex', alignItems: 'center' }}>
                <Hash size={14} color="#737373" />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#737373',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    fontSize: '0.8rem',
                  }}
                >
                  EDITING CLIP_{editingModel.id}
                </Typography>
              </Box>
            </Box>

            {/* Main Content */}
            <Stack spacing={2}>
              {/* Name Field */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#525252',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.68rem',
                    display: 'block',
                    mb: 0.25,
                  }}
                >
                  Name *
                </Typography>
                <HookFormTextField
                  name={nameOfForm('note')}
                  label=""
                  disabled={disabled}
                  required
                  size="small"
                  margin="none"
                  variant="standard"
                  sx={{
                    '& .MuiInputBase-root': {
                      color: '#e5e5e5',
                      backgroundColor: 'transparent',
                      '&::before': {
                        borderBottom: '1px solid #262626',
                      },
                      '&:hover::before': {
                        borderBottom: '1px solid #525252',
                      },
                    },
                  }}
                />
              </Box>

              {/* Camera Mode Selection */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#525252',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.68rem',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  Camera Mode *
                </Typography>
                <ToggleButtonGroup
                  value={form.watch(nameOfForm('cameraMode'))}
                  exclusive
                  onChange={handleCameraModeChange}
                  disabled={disabled}
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: '#737373',
                      borderColor: '#262626',
                      '&.Mui-selected': {
                        backgroundColor: '#1e40af',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#1e3a8a',
                        },
                      },
                      '&:hover': {
                        backgroundColor: '#171717',
                      },
                    },
                  }}
                >
                  <ToggleButton value="tracked">Tracked Object</ToggleButton>
                  <ToggleButton value="fixed">Fixed Camera</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Tracked Object - Only visible in 'tracked' mode */}
              {form.watch(nameOfForm('cameraMode')) === 'tracked' && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#525252',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mt: 1,
                    }}
                  >
                    Tracked Object *
                  </Typography>
                  <TrackedObject
                    disabled={disabled}
                    onClick={handleClick}
                    readOnly={false}
                    clip={{
                      ...editingModel,
                      objectTrackingId: form.watch(nameOfForm('objectTrackingId')),
                    }}
                    teams={teams}
                    onChange={(objectTrackingId: number | null) => {
                      try {
                        if (objectTrackingId !== null) {
                          handleChangeTrackedEntity(objectTrackingId);
                          setTrackedObjectError(null); // Clear error on success
                        }
                      } catch (error) {
                        // Handle tracked object switching errors
                        const errorMessage =
                          error instanceof Error
                            ? error.message
                            : 'Failed to change tracked object';
                        setTrackedObjectError(errorMessage);
                      }
                    }}
                  />
                  {/* Show validation error */}
                  {form.formState.errors.objectTrackingId && (
                    <Box sx={{ color: '#f87171', fontSize: '0.75rem', mt: 1 }}>
                      {form.formState.errors.objectTrackingId.message}
                    </Box>
                  )}
                  {/* Show runtime error from switching */}
                  {trackedObjectError && (
                    <Box sx={{ color: '#f87171', fontSize: '0.75rem', mt: 1 }}>
                      {trackedObjectError}
                    </Box>
                  )}
                </Box>
              )}

              {/* Camera Selection */}
              <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                <Videocam size={18} color="#525252" />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#525252',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Camera *
                  </Typography>
                  <PlayerCameraFeedSelectionButton
                    value={clip?.cameraId}
                    onChange={handleCameraChange}
                    error={!!form.formState.errors.cameraId}
                    disabled={disabled}
                    onError={(message) => {
                      if (message === undefined) {
                        form.clearErrors(nameOfForm('cameraId'));
                      } else {
                        form.setError(nameOfForm('cameraId'), {
                          type: 'manual',
                          message: message,
                        });
                      }
                    }}
                    trackingOrigin="Clip Record Form"
                  />
                  {form.formState.errors.cameraId && (
                    <Box sx={{ color: '#f87171', fontSize: '0.75rem', mt: 1 }}>
                      {form.formState.errors.cameraId.message}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#525252',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.68rem',
                    display: 'block',
                    mb: 0.25,
                  }}
                >
                  Description
                </Typography>
                <HookFormTextField
                  name={nameOfForm('description')}
                  label=""
                  margin="none"
                  variant="standard"
                  disabled={disabled}
                  multiline
                  rowsMax={3}
                  autoResize={true}
                  helperText={`${descriptionLength.toLocaleString()}/${DESCRIPTION_MAX.toLocaleString()}`}
                  FormHelperTextProps={{
                    sx: () => ({
                      textAlign: 'right',
                      color: descriptionLength > DESCRIPTION_MAX ? '#f87171' : '#737373',
                    }),
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      color: '#e5e5e5',
                      backgroundColor: 'transparent',
                      '&::before': {
                        borderBottom: '1px solid #262626',
                      },
                      '&:hover::before': {
                        borderBottom: '1px solid #525252',
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </form>
        </FormProvider>
      </CardContent>

      {/* Action Buttons */}
      <CardActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 0.5,
          borderTop: '1px solid #262626',
          py: 1.5,
          px: 2,
          backgroundColor: '#050505',
        }}
      >
        {[
          {
            icon: TimesIcon,
            label: isLocalClip ? 'Cancel' : 'Cancel Changes',
            color: 'error',
            variant: 'outlined',
            handler: handleCancel,
            condition: true,
          },
          {
            icon: SaveIcon,
            label: isLocalClip ? 'Save' : 'Save Changes',
            color: 'success',
            variant: 'contained',
            handler: handleSubmit,
            condition: true,
          },
        ]
          .filter((button) => button.condition)
          .map((button) => {
            const Icon = button.icon;
            return (
              <Button
                key={button.label}
                onClick={(e) => {
                  e.stopPropagation();
                  if (button.handler) button.handler();
                }}
                size="small"
                variant={button.variant as any}
                startIcon={<Icon size={16} strokeWidth={1.5} />}
                color={button.color as any}
                disabled={disabled}
                sx={{
                  minWidth: 'auto',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {button.label}
              </Button>
            );
          })}
      </CardActions>
    </Card>
  );
};

export default ClipRecordForm;
